import { useCallback, useEffect, useReducer } from "react";
import { toast } from "sonner";
import { docContentBaseUrl, DocumentService } from "@/services/api/DocumentService";
import { PDFService } from "@/services/knowledgebase/PDFService";
import {
  ContentAction,
  ContentState,
  initialContentState,
} from "@/types/knowledge/document-content";
import { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

const contentReducer = (state: ContentState, action: ContentAction): ContentState => {
  switch (action.type) {
    case "SET_VIEW_MODE":
      return { ...state, viewMode: action.payload };
    case "SET_CONTENT":
      return { ...state, urlContent: action.payload.urlContent };
    case "SET_OPTIMIZED_CONTENT":
      return { ...state, optimizedContent: action.payload.optimizedContent };
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };
    case "SET_OPTIMIZING":
      return { ...state, isOptimizing: action.payload };
    case "SET_CONTENT_PAGE":
      return { ...state, contentPage: action.payload };
    case "SET_PDF_METADATA":
      return {
        ...state,
        totalPages: action.payload.totalPages ?? state.totalPages,
        totalParts: action.payload.totalParts ?? state.totalParts,
        hasMetadataJson: action.payload.hasMetadataJson,
      };
    case "UPDATE_PDF_PROGRESS":
      return {
        ...state,
        isProcessing: action.payload.isProcessing,
        processingProgress: action.payload.processingProgress,
      };
    case "SET_CURRENT_PART":
      return { ...state, currentPart: action.payload };
    case "SET_CURRENT_PAGE":
      return { ...state, currentPage: action.payload };
    case "RESET_ALL":
      return initialContentState;
    case "RESET_ON_DOCUMENT_CHANGE":
      return {
        ...state,
        urlContent: null,
        optimizedContent: null,
        viewMode: "formatted",
        contentPage: 1,
      };
    default:
      return state;
  }
};

export const useDocumentContent = (document: DocumentWithMetadata | null, isOpen: boolean) => {
  const [state, dispatch] = useReducer(contentReducer, initialContentState);
  const CHARS_PER_PAGE = 10000; // ~10KB of text per page

  const isPdf = document ? PDFService.isPdf(document) : false;

  // PDF-specific URL resolution
  const resolveDocumentUrl = useCallback(async (): Promise<string | null> => {
    if (!document) return null;
    if (document.parsed_path) return document.parsed_path;

    try {
      const fullDocument = await DocumentService.getKnowledgeBaseDocument(document.id);
      if (fullDocument?.parsed_path) {
        return fullDocument.parsed_path;
      }
    } catch (error) {
      console.warn("Failed to hydrate full document while resolving PDF url:", error);
    }

    return document.id ? `${docContentBaseUrl}${document.id}` : null;
  }, [document]);

  const getCurrentPageContent = useCallback(
    (content: string | null) => {
      if (!content) return null;
      if (isPdf) return content;
      if (content.length <= CHARS_PER_PAGE) return content;

      const startIndex = (state.contentPage - 1) * CHARS_PER_PAGE;
      const endIndex = startIndex + CHARS_PER_PAGE;
      return content.slice(startIndex, endIndex);
    },
    [isPdf, state.contentPage],
  );

  const getTotalPages = useCallback(
    (content: string | null) => {
      if (isPdf) return state.totalPages || 1;
      if (!content || content.length <= CHARS_PER_PAGE) return 1;
      return Math.ceil(content.length / CHARS_PER_PAGE);
    },
    [isPdf, state.totalPages],
  );

  const needsPagination = useCallback(
    (content: string | null) => {
      if (isPdf) return false;
      return content && content.length > CHARS_PER_PAGE;
    },
    [isPdf],
  );

  // Helper to build URL from path (handles both S3 and Supabase storage)
  const buildUrlFromPath = useCallback(
    async (basePath: string, filePath: string): Promise<string | null> => {
      if (!basePath) return null;

      // If basePath is S3 URL, append filePath directly
      if (basePath.includes("fs.enterprisedna.co")) {
        return `${basePath}/${filePath}`;
      }

      // For Supabase storage, need signed URL
      const fullPath = `${basePath}/${filePath}`;
      return DocumentService.getSignedUrl(fullPath);
    },
    [],
  );

  const fetchDocumentContent = useCallback(
    async (contentType: "pdf" | "text" = isPdf ? "pdf" : "text") => {
      if (!isOpen || !document) return;

      const isPdfType = contentType === "pdf";
      if (isPdfType && !isPdf) return;
      if (!isPdfType && isPdf) return;

      try {
        console.log(`[DocContent] Loading ${contentType}:`, {
          id: document.id,
          name: document.name,
          parsedPath: document.parsed_path,
          hasEditedUrl: !!document.metadata?.edited_url,
        });

        if (document.metadata?.edited_url) {
          const response = await fetch(document.metadata.edited_url);
          if (response.ok) {
            const text = await response.text();
            dispatch({ type: "SET_CONTENT", payload: { urlContent: text } });
            if (isPdfType) {
              dispatch({
                type: "UPDATE_PDF_PROGRESS",
                payload: { isProcessing: false, processingProgress: 100 },
              });
            }
            return;
          }
        }

        if (isPdfType) {
          const resolvedDocumentUrl = await resolveDocumentUrl();
          if (!resolvedDocumentUrl) {
            dispatch({
              type: "SET_CONTENT",
              payload: { urlContent: "Unable to locate document content URL." },
            });
            return;
          }

          const metadataUrl = await buildUrlFromPath(resolvedDocumentUrl, "metadata.json");
          if (!metadataUrl) {
            dispatch({
              type: "SET_CONTENT",
              payload: { urlContent: "Unable to access document metadata." },
            });
            return;
          }
          const metadataResponse = await fetch(metadataUrl);

          if (metadataResponse.ok) {
            const s3Metadata = await metadataResponse.json();
            dispatch({
              type: "SET_PDF_METADATA",
              payload: {
                totalPages: s3Metadata.total_pages || null,
                totalParts: s3Metadata.parts || 1,
                hasMetadataJson: true,
              },
            });

            const parsedUrl = await buildUrlFromPath(resolvedDocumentUrl, `parsed_${state.currentPart}.txt`);
            if (!parsedUrl) {
              dispatch({
                type: "SET_CONTENT",
                payload: { urlContent: "Unable to access parsed content." },
              });
              return;
            }
            const contentResponse = await fetch(parsedUrl);

            if (contentResponse.ok) {
              const text = await contentResponse.text();
              dispatch({ type: "SET_CONTENT", payload: { urlContent: text } });

              const optimizedUrl = await buildUrlFromPath(resolvedDocumentUrl, `optimized_${state.currentPart}.txt`);
              if (optimizedUrl) {
                const optimizedResponse = await fetch(optimizedUrl);
                if (optimizedResponse.ok) {
                  const optimizedText = await optimizedResponse.text();
                  if (optimizedText?.trim().length > 0) {
                    dispatch({
                      type: "SET_OPTIMIZED_CONTENT",
                      payload: { optimizedContent: optimizedText },
                    });
                  }
                }
              }

              const isCreatedToday =
                document.created_at &&
                new Date(document.created_at).toDateString() === new Date().toDateString();

              if (isCreatedToday && s3Metadata.status === "in_progress") {
                let availablePartsCount = 1;
                for (let i = state.currentPart + 1; i <= (s3Metadata.parts || 1); i++) {
                  try {
                    const checkResponse = await fetch(`${resolvedDocumentUrl}/parsed_${i}.txt`, {
                      method: "HEAD",
                    });
                    if (checkResponse.ok) {
                      availablePartsCount = i;
                    } else {
                      break;
                    }
                  } catch {
                    break;
                  }
                }
                const progress = Math.round((availablePartsCount / (s3Metadata.parts || 1)) * 100);
                dispatch({
                  type: "UPDATE_PDF_PROGRESS",
                  payload: {
                    isProcessing: availablePartsCount < (s3Metadata.parts || 1),
                    processingProgress: Math.min(98, progress),
                  },
                });
              } else {
                dispatch({
                  type: "UPDATE_PDF_PROGRESS",
                  payload: { isProcessing: false, processingProgress: 100 },
                });
              }
            } else {
              dispatch({
                type: "SET_CONTENT",
                payload: {
                  urlContent:
                    "The content is being parsed. Please come back to this page in a few moments.",
                },
              });
            }
          } else {
            dispatch({
              type: "SET_CONTENT",
              payload: {
                urlContent:
                  "Content could not be loaded. The document may still be processing — try refreshing in a moment.",
              },
            });
          }
        } else {
          const pathToUse = document.parsed_path;
          if (!pathToUse) return;

          const signedUrl = await DocumentService.getSignedUrl(pathToUse);
          // console.log(`[DocContent] Resolved ${contentType} URL:`, signedUrl);
          if (!signedUrl) {
            dispatch({
              type: "SET_CONTENT",
              payload: { urlContent: "Unable to access document content." },
            });
            return;
          }

          const response = await fetch(signedUrl);
          if (response.ok) {
            const text = await response.text();
            dispatch({ type: "SET_CONTENT", payload: { urlContent: text } });
          } else {
            dispatch({
              type: "SET_CONTENT",
              payload: { urlContent: "Error loading document content." },
            });
          }
        }
      } catch (error) {
        console.error(`[DocContent] Error loading ${contentType}:`, error);
        dispatch({
          type: "SET_CONTENT",
          payload: { urlContent: `Error loading ${contentType} content.` },
        });
        if (isPdfType) {
          dispatch({
            type: "SET_PDF_METADATA",
            payload: { hasMetadataJson: false },
          });
          dispatch({
            type: "UPDATE_PDF_PROGRESS",
            payload: { isProcessing: false, processingProgress: 0 },
          });
        }
      }
    },
    [isOpen, document, isPdf, state.currentPart, resolveDocumentUrl, buildUrlFromPath],
  );

  const fixDocument = async (
    fullDocument: boolean,
    onDocumentUpdate?: (
      document: DocumentWithMetadata,
      updates: Partial<DocumentWithMetadata>,
    ) => Promise<any>,
  ) => {
    if (!document?.id) {
      toast.error("Document ID is missing, cannot optimize.");
      return;
    }

    if (!state.urlContent) {
      toast.error("No content available to optimize.");
      return;
    }

    dispatch({ type: "SET_OPTIMIZING", payload: true });
    const fixToast = toast.loading(`Optimizing ${isPdf ? "PDF" : "document"} content with AI...`);

    try {
      let optimizedText: string;

      if (isPdf) {
        const partNumber = fullDocument ? null : state.currentPart;
        optimizedText = await PDFService.optimizeDocument(document.id, partNumber);
      } else {
        const pathToUse = document.parsed_path;
        const pathParts = pathToUse!.split(".");
        const extension = pathParts.pop();
        const optimizedFilePath = `${pathParts.join(".")}_optimized.${extension}`;
        optimizedText = await PDFService.optimizeTextContent(
          state.urlContent,
          undefined,
          optimizedFilePath,
        );
      }

      dispatch({ type: "SET_OPTIMIZED_CONTENT", payload: { optimizedContent: optimizedText } });
      dispatch({ type: "SET_VIEW_MODE", payload: "optimized" });

      const metadataUpdate = { metadata: { optimized: true } };

      if (onDocumentUpdate) {
        await onDocumentUpdate(document, metadataUpdate);
      } else {
        await updateDocumentOptimizedStatus(document);
      }

      toast.success(`Document content optimized successfully!`, {
        id: fixToast,
      });
    } catch (error: any) {
      console.error("Failed to optimize document:", error);
      toast.error(`Failed to optimize document: ${error.message || "Unknown error"}`, {
        id: fixToast,
      });
    } finally {
      dispatch({ type: "SET_OPTIMIZING", payload: false });
    }
  };

  const updateDocumentOptimizedStatus = async (doc: DocumentWithMetadata) => {
    try {
      if (doc.id) {
        await DocumentService.updateKnowledgeBaseDocument(doc.id, {
          metadata: {
            ...doc.metadata,
            optimized: true,
          },
        });
        console.log("Updated document metadata optimization status");
      }
    } catch (error) {
      console.error("Error updating document metadata:", error);
    }
  };

  const loadOptimizedContent = useCallback(async () => {
    if (!document) return;

    dispatch({ type: "SET_LOADING", payload: true });
    const noOptimizedMsg = "No optimized content available. Click 'Optimize' to generate it.";

    try {
      if (isPdf) {
        const resolvedDocumentUrl = document.optimized_path;
        if (resolvedDocumentUrl) {
          const optimizedUrl = await buildUrlFromPath(resolvedDocumentUrl, `optimized_${state.currentPart}.txt`);
          if (optimizedUrl) {
            const response = await fetch(optimizedUrl);
            if (response.ok) {
              const optimizedText = await response.text();
              dispatch({
                type: "SET_OPTIMIZED_CONTENT",
                payload: { optimizedContent: optimizedText },
              });
            } else {
              dispatch({
                type: "SET_OPTIMIZED_CONTENT",
                payload: { optimizedContent: noOptimizedMsg },
              });
            }
          } else {
            dispatch({
              type: "SET_OPTIMIZED_CONTENT",
              payload: { optimizedContent: noOptimizedMsg },
            });
          }
        } else {
          dispatch({
            type: "SET_OPTIMIZED_CONTENT",
            payload: { optimizedContent: noOptimizedMsg },
          });
        }
      } else {
        const optimizedPathToUse =
          document.optimized_path || document.metadata?.optimized_file_path;
        if (optimizedPathToUse) {
          const signedUrl = await DocumentService.getSignedUrl(optimizedPathToUse);
          if (signedUrl) {
            const response = await fetch(signedUrl);
            if (response.ok) {
              const optimizedText = await response.text();
              dispatch({
                type: "SET_OPTIMIZED_CONTENT",
                payload: { optimizedContent: optimizedText },
              });
            } else {
              dispatch({
                type: "SET_OPTIMIZED_CONTENT",
                payload: { optimizedContent: noOptimizedMsg },
              });
            }
          } else {
            dispatch({
              type: "SET_OPTIMIZED_CONTENT",
              payload: { optimizedContent: noOptimizedMsg },
            });
          }
        } else {
          dispatch({
            type: "SET_OPTIMIZED_CONTENT",
            payload: { optimizedContent: noOptimizedMsg },
          });
        }
      }
    } catch (error) {
      console.error("Error loading optimized content:", error);
      dispatch({ type: "SET_OPTIMIZED_CONTENT", payload: { optimizedContent: noOptimizedMsg } });
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [document, isPdf, state.currentPart, resolveDocumentUrl, buildUrlFromPath]);

  const refreshContent = useCallback(
    async (skipToast = false) => {
      dispatch({ type: "SET_LOADING", payload: true });
      const refreshToast = skipToast
        ? null
        : toast.loading(`Refreshing ${state.viewMode} content...`);

      try {
        if (state.viewMode === "formatted") {
          await fetchDocumentContent();
        } else {
          await loadOptimizedContent();
        }
        if (!skipToast && refreshToast) {
          toast.success("Content refreshed successfully", { id: refreshToast });
        }
      } catch (error) {
        console.error("Error refreshing content:", error);
        if (!skipToast && refreshToast) {
          toast.error(`Failed to refresh content: ${error?.message || "Unknown error"}`, {
            id: refreshToast,
          });
        }
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    },
    [state.viewMode, fetchDocumentContent, loadOptimizedContent],
  );

  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: "RESET_ALL" });
    } else if (document) {
      dispatch({ type: "RESET_ON_DOCUMENT_CHANGE" });
    }
  }, [document?.id, isOpen]);

  useEffect(() => {
    if (!isOpen || !document) return;

    dispatch({ type: "SET_LOADING", payload: true });
    (async () => {
      try {
        if (state.viewMode === "optimized") {
          await loadOptimizedContent();
        } else {
          await fetchDocumentContent();
        }
      } catch (error) {
        console.error("Error loading content:", error);
      } finally {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    })();
  }, [document?.id, state.viewMode, isOpen, fetchDocumentContent, loadOptimizedContent]);

  useEffect(() => {
    if (!isOpen || !isPdf || !state.isProcessing || !document) return;

    const intervalId = setInterval(async () => {
      try {
        await fetchDocumentContent("pdf");
      } catch (error) {
        console.error("Error during polling:", error);
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [isOpen, isPdf, state.isProcessing, document?.id, fetchDocumentContent]);

  return {
    // State
    viewMode: state.viewMode,
    setViewMode: (mode: "formatted" | "optimized") =>
      dispatch({ type: "SET_VIEW_MODE", payload: mode }),
    urlContent: state.urlContent,
    setUrlContent: (content: string | null) =>
      dispatch({ type: "SET_CONTENT", payload: { urlContent: content } }),
    optimizedContent: state.optimizedContent,
    setOptimizedContent: (content: string | null) =>
      dispatch({ type: "SET_OPTIMIZED_CONTENT", payload: { optimizedContent: content } }),
    isLoading: state.isLoading,
    isOptimizing: state.isOptimizing,
    contentPage: state.contentPage,
    setContentPage: (page: number) => dispatch({ type: "SET_CONTENT_PAGE", payload: page }),
    CHARS_PER_PAGE,
    isProcessing: state.isProcessing,
    processingProgress: state.processingProgress,
    currentPage: state.currentPage,
    totalPages: state.totalPages,
    totalParts: state.totalParts,
    currentPart: state.currentPart,
    setCurrentPart: (part: number) => dispatch({ type: "SET_CURRENT_PART", payload: part }),
    hasMetadataJson: state.hasMetadataJson,

    // Functions
    getCurrentPageContent,
    getTotalPages,
    needsPagination,
    refreshContent,
    fixDocument,
    loadOptimizedContent,
  };
};
